'use client';

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/navigation';
import { Layout } from '@/components/layout/Layout';
import { Hero } from '@/components/sections/Hero';
import { WhatWeDo } from '@/components/sections/WhatWeDo';
import { Benefits } from '@/components/sections/Benefits';
import { DemoVideo } from '@/components/sections/DemoVideo';
import { DashboardShowcase } from '@/components/sections/DashboardShowcase';
import { HowTo } from '@/components/sections/HowTo';
import { Form } from '@/components/ui/Form';
import { clearFormDraft } from '@/components/ui/formDraft';
import { SubscriptionTierSelector } from '@/components/ui/SubscriptionTierSelector';
import { LoadingOverlay } from '@/components/ui/LoadingOverlay';
import { ExtendedFormData, PricingTierId, BillingType, PRICING_TIERS } from '@/types';
import { trackBeginCheckout } from '@/components/Analytics';
import { Testimonials } from '@/components/sections/Testimonials';
import { Evidence } from '@/components/sections/Evidence';
import { HomeFaq } from '@/components/sections/HomeFaq';
import { getCsrfToken } from '@/lib/csrf';
import { getAttribution, hasFullConsent } from '@/lib/attribution';
import i18next from 'i18next';

export function HomeContent() {
  const { t } = useTranslation();
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTier, setSelectedTier] = useState<PricingTierId>('professional');
  const [selectedBilling, setSelectedBilling] = useState<BillingType>('monthly');
  const [resubscribeData, setResubscribeData] = useState<any>(null);
  const [isResubscribeMode, setIsResubscribeMode] = useState(false);

  // First-touch attribution capture — must run on the very first landing so
  // an ad click isn't overwritten by whatever page the visitor browses to
  // next. No-ops if attribution was already captured earlier this session.
  useEffect(() => {
  }, []);

  // Check for resubscribe mode on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('resubscribe') === 'true') {
      setIsResubscribeMode(true);
      // Fetch resubscribe data
      fetch('/api/resubscribe-data', {
        credentials: 'include'
      })
        .then(res => res.json())
        .then(data => {
          if (!data.error) {
            setResubscribeData(data);
            if (data.tier) {
              setSelectedTier(data.tier as PricingTierId);
            }
          }
        })
        .catch(err => console.error('Failed to load resubscribe data:', err));
    }
  }, []);

  const handleTierSelect = (tier: PricingTierId, billing: BillingType) => {
    setSelectedTier(tier);
    setSelectedBilling(billing);
    // Update URL params without navigation
    const url = new URL(window.location.href);
    url.searchParams.set('tier', tier);
    url.searchParams.set('billing', billing);
    window.history.replaceState({}, '', url.toString());
  };

  const handleFormSubmit = async (data: ExtendedFormData) => {
    console.log('🔵 Form submitted - uploading deck, then starting checkout');
    setIsLoading(true);
    
    try {
      // The deck goes up WITH the checkout request, before the redirect to
      // Stripe. It used to be parked in this browser's IndexedDB and uploaded
      // by the /success page after payment, which lost the order outright if
      // the buyer never made it back to that page.
      console.log('📝 Creating checkout session (deck included)...');
      const formData = new window.FormData();
      formData.append('email', data.email);
      if (data.comment) formData.append('comment', data.comment);
      if (data.name) formData.append('name', data.name);
      if (data.industry) formData.append('industry', data.industry);
      if (data.phone) formData.append('phone', data.phone);
      if (data.description) formData.append('description', data.description);
      if (data.tier) formData.append('tier', data.tier);
      if (data.billingType) formData.append('billingType', data.billingType);
      if (data.couponCode) formData.append('couponCode', data.couponCode);
      if (data.selectedUpsells && data.selectedUpsells.length > 0) {
        formData.append('selectedUpsells', data.selectedUpsells.join(','));
      }
      
      // Create file metadata for the webhook
      const fileMetadata = JSON.stringify(data.files.map(file => ({
        name: file.name,
        size: file.size,
        type: file.type,
      })));
      formData.append('fileMetadata', fileMetadata);

      if (data.files[0]) {
        formData.append('file', data.files[0]);
      }
      
      // Attach first-touch attribution (captured on mount) so it lands in
      // the Stripe session's metadata and finally connects a paying
      // customer back to the campaign that acquired them.
      const attribution = getAttribution();
      if (attribution) {
        formData.append('attribution', JSON.stringify(attribution));
      }

      // Tells the server whether the server-side GA4 purchase event may be sent
      // for this checkout. Omitted (not '0') without marketing consent.
      if (hasFullConsent()) {
        formData.append('analytics_consent', '1');
      }

      // Add isResubscribe flag for resubscribe flow. When no new deck is
      // attached the server resolves the existing one from the account itself
      // — a client-supplied blob URL is not trusted for that.
      if (isResubscribeMode) {
        formData.append('isResubscribe', 'true');
      }
      
      console.log('🚀 Calling /api/create-checkout-session...');

      const tierForTracking = data.tier ?? selectedTier;
      const billingForTracking = data.billingType ?? 'monthly';
      const tierMeta = PRICING_TIERS.find(t => t.id === tierForTracking);
      const trackingValue = tierMeta
        ? (billingForTracking === 'yearly' ? tierMeta.priceYearly : tierMeta.priceMonthly) / 100
        : 0;
      trackBeginCheckout(tierForTracking, trackingValue, billingForTracking);

      // Get CSRF token
      const csrfToken = getCsrfToken();

      // Submit to Stripe for payment
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken || '',
        },
        body: formData,
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }
      
      const { url } = await response.json();

      console.log('✅ Deck stored server-side, redirecting to Stripe...');
      
      // The checkout session exists, so the saved draft has served its purpose
      // — drop the founder's contact details from this browser rather than
      // leaving them behind on the machine. Deliberately after the response
      // check: a failed checkout keeps the draft so nothing is retyped.
      clearFormDraft();

      // Store current language in sessionStorage before redirect
      sessionStorage.setItem('i18nextLng_pre_stripe', i18next.language);
      
      // Redirect to Stripe checkout
      window.location.href = url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      const errorMessage = error instanceof Error ? error.message : t('pageContent.home.uploadSection.form.errors.processing');
      alert(errorMessage);
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <Hero />
      <HowTo />

      <section id="upload-section" className="py-16 md:py-24 bg-gray-50">
        <div className="container">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {t('pageContent.home.uploadSection.title')}
              </h2>
              <p className="text-xl text-gray-600">
                {t('pageContent.home.uploadSection.subtitle')}
              </p>
            </div>
            
            {/* Subscription Tier Selector */}
            <div className="mb-8">
              <SubscriptionTierSelector 
                onSelect={handleTierSelect}
                defaultTier={selectedTier}
                defaultBilling={selectedBilling}
              />
            </div>

            {/* Form */}
            <div className="bg-white p-8 md:p-10 rounded-xl shadow-sm border border-gray-100">
              {isResubscribeMode && resubscribeData && (
                <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-blue-900 mb-2">Welcome Back!</h3>
                  <p className="text-sm text-blue-800">
                    We&apos;ve pre-filled your information. {resubscribeData.hasPdf ? 'Your existing pitch deck will be used, or you can upload a new one below (this will replace the old one).' : 'Please upload your pitch deck below.'}
                  </p>
                </div>
              )}
              <Form 
                onSubmit={handleFormSubmit} 
                loading={isLoading}
                selectedTier={selectedTier}
                selectedBilling={selectedBilling}
                resubscribeData={resubscribeData}
                isResubscribeMode={isResubscribeMode}
              />
            </div>
          </div>
        </div>
      </section>
      <WhatWeDo />
      <DashboardShowcase />
      <Benefits />
      {/* Directly after Benefits, as asked: that section explains how the
          matching works, and the demo shows it. */}
      <DemoVideo />
      {/* Evidence before the FAQ, and both before the closing CTA: the cited
          research explains WHY the product is shaped this way, the FAQ answers
          the objections that raises, and Testimonials carries the final CTA.
          The FAQ's six answers are mirrored verbatim in the FAQPage JSON-LD
          emitted by app/(routes)/page.tsx — both read HOME_FAQ_KEYS, so they
          cannot drift apart. */}
      <Evidence />
      <HomeFaq />
      <Testimonials />
      {/* Loading Overlay */}
      <LoadingOverlay 
        isLoading={isLoading} 
        message={t('pageContent.home.uploadSection.form.processing')} 
      />
    </Layout>
  );
} 